/**
 * Native-feel helpers — thin wrappers around Capacitor plugins that
 * silently no-op on web so the same code works in both environments.
 *
 * Used to add the "this is a real iOS app" polish that satisfies App
 * Store reviewers and just plain feels nicer to use:
 *   • hapticTap()    — tiny tactile bump on prediction taps, tab swaps, etc.
 *   • hapticImpact() — medium bump for confirms / opens
 *   • hapticSuccess()/Warning()/Error() — system-style notification feedback
 *   • shareNative()  — native iOS share sheet (Messages, Mail, Twitter, …)
 *                      falls back to navigator.share or copy-to-clipboard.
 */

const isNative = () => typeof window !== 'undefined'
  && window.Capacitor
  && typeof window.Capacitor.isNativePlatform === 'function'
  && window.Capacitor.isNativePlatform();

export const hapticTap = async () => {
  if (!isNative()) return;
  try {
    const { Haptics, ImpactStyle } = await import('@capacitor/haptics');
    await Haptics.impact({ style: ImpactStyle.Light });
  } catch { /* no-op */ }
};

export const hapticImpact = async () => {
  if (!isNative()) return;
  try {
    const { Haptics, ImpactStyle } = await import('@capacitor/haptics');
    await Haptics.impact({ style: ImpactStyle.Medium });
  } catch { /* no-op */ }
};

export const hapticSuccess = async () => {
  if (!isNative()) return;
  try {
    const { Haptics, NotificationType } = await import('@capacitor/haptics');
    await Haptics.notification({ type: NotificationType.Success });
  } catch { /* no-op */ }
};

export const hapticWarning = async () => {
  if (!isNative()) return;
  try {
    const { Haptics, NotificationType } = await import('@capacitor/haptics');
    await Haptics.notification({ type: NotificationType.Warning });
  } catch { /* no-op */ }
};

export const hapticError = async () => {
  if (!isNative()) return;
  try {
    const { Haptics, NotificationType } = await import('@capacitor/haptics');
    await Haptics.notification({ type: NotificationType.Error });
  } catch { /* no-op */ }
};

/**
 * Native share sheet → falls through to Web Share API → falls through
 * to clipboard copy. Returns `{shared, method}` so the caller can show
 * the right toast (e.g. "Copied!" when no real sheet was available).
 */
export const shareNative = async ({ title, text, url, dialogTitle }) => {
  // Capacitor first (best UX on iOS — opens the real Share Sheet).
  if (isNative()) {
    try {
      const { Share } = await import('@capacitor/share');
      await Share.share({ title, text, url, dialogTitle: dialogTitle || title });
      return { shared: true, method: 'native' };
    } catch (e) {
      // User cancelled the sheet → not an error from our POV.
      if (String(e?.message || '').toLowerCase().includes('cancel')) {
        return { shared: false, method: 'native', cancelled: true };
      }
      // Fall through to web fallback on any other error.
    }
  }

  // Web Share API (works on most modern mobile browsers).
  try {
    if (typeof navigator !== 'undefined' && navigator.share) {
      await navigator.share({ title, text, url });
      return { shared: true, method: 'web-share' };
    }
  } catch { /* user cancelled or unsupported */ }

  // Final fallback — copy to clipboard.
  try {
    if (typeof navigator !== 'undefined' && navigator.clipboard) {
      await navigator.clipboard.writeText(`${text || title}\n${url || ''}`.trim());
      return { shared: true, method: 'clipboard' };
    }
  } catch { /* swallow */ }

  return { shared: false, method: 'none' };
};

/**
 * Open a URL in an in-app browser on native (SFSafariViewController on iOS —
 * full YouTube playback, stays inside the app), or a new tab on web.
 *
 * Used as a belt-and-braces fallback for highlight videos whose owner has
 * fully disabled embedding (YouTube error 101/150) — the inline player can't
 * show those, but this always plays them.
 */
export const openExternalUrl = async (url) => {
  if (!url) return;
  if (isNative()) {
    try {
      const { Browser } = await import('@capacitor/browser');
      await Browser.open({ url, presentationStyle: 'popover' });
      return;
    } catch { /* fall through to window.open */ }
  }
  try {
    window.open(url, '_blank', 'noopener,noreferrer');
  } catch { /* swallow */ }
};
